import React, { useState } from 'react';
import axios from 'axios';
import './InvoiceViewer.css';

function InvoiceViewer() {
  const [clientId, setClientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientData, setClientData] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!clientId || clientId.trim() === '') {
      setError('Por favor ingrese su ID de cliente');
      return;
    }

    setLoading(true);
    setError('');
    setClientData(null);
    setInvoices([]);

    try {
      const response = await axios.post('/api/invoices/get-client-invoices', {
        clientId: clientId.trim()
      });

      if (response.data.success) {
        setClientData(response.data.client);
        setInvoices(response.data.invoices || []);
        
        if (!response.data.invoices || response.data.invoices.length === 0) {
          setError('No se encontraron facturas para este cliente');
        }
      } else {
        setError(response.data.message || 'No se pudo obtener la información del cliente');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.message || 'Error al consultar las facturas. Verifique su ID de cliente.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return 'N/A';
    
    try {
      const [year, month, day] = dateString.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('es-DO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    
    if (statusLower === 'pagada' || statusLower === 'pagado') {
      return <span className="status-badge paid">✅ Pagada</span>;
    } else if (statusLower === 'no pagado' || statusLower === 'pendiente') {
      return <span className="status-badge pending">⏳ Pendiente</span>;
    } else {
      return <span className="status-badge">{status}</span>;
    }
  };

  return (
    <div className="invoice-viewer">
      <div className="viewer-container">
        <div className="header">
          <h1>🧾 Consulta de Facturas</h1>
          <p>Ingrese su ID de cliente para ver sus facturas</p>
        </div>

        <form onSubmit={handleSubmit} className="search-form">
          <div className="input-group">
            <input
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Ingrese su ID de cliente (ej: 123)"
              className="client-id-input"
              disabled={loading}
            />
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? '🔍 Buscando...' : '🔍 Buscar'}
            </button>
          </div>
          {error && <div className="error-message">❌ {error}</div>}
        </form>

        {clientData && (
          <div className="client-info">
            <h2>👤 Información del Cliente</h2>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">ID:</span>
                <span className="value">{clientData.id}</span>
              </div>
              <div className="info-item">
                <span className="label">Nombre:</span>
                <span className="value">{clientData.nombre}</span>
              </div>
              <div className="info-item">
                <span className="label">Email:</span>
                <span className="value">{clientData.email || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">Teléfono:</span>
                <span className="value">{clientData.telefono || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}

        {invoices.length > 0 && (
          <div className="invoices-section">
            <h2>📋 Facturas ({invoices.length})</h2>
            <div className="invoices-list">
              {invoices.map((invoice, index) => (
                <div key={index} className="invoice-card">
                  <div className="invoice-header">
                    <div className="invoice-number">
                      <span className="label">Factura #</span>
                      <span className="number">{invoice.id || invoice.idfactura}</span>
                    </div>
                    {getStatusBadge(invoice.estado)}
                  </div>
                  
                  <div className="invoice-details">
                    <div className="detail-row">
                      <span className="label">💰 Monto:</span>
                      <span className="value amount">{formatCurrency(invoice.monto || invoice.total)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">📅 Fecha Creación:</span>
                      <span className="value">{formatDate(invoice.fechacreacion || invoice.fecha)}</span>
                    </div>
                    {invoice.vencimiento && invoice.vencimiento !== '0000-00-00' && (
                      <div className="detail-row">
                        <span className="label">⏰ Fecha Vencimiento:</span>
                        <span className="value">{formatDate(invoice.vencimiento)}</span>
                      </div>
                    )}
                    {invoice.descripcion && (
                      <div className="detail-row">
                        <span className="label">📝 Descripción:</span>
                        <span className="value">{invoice.descripcion}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {clientData && invoices.length === 0 && !error && (
          <div className="no-invoices">
            <p>✅ No hay facturas pendientes o registradas para este cliente</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default InvoiceViewer;
